/**
 * フォームの強制変換設定の一覧
 */
export const validateType = {
    numerics: "$Numerics$",
    realNumber: "$RealNumber$",
    alphabetic: "$Alphabetic$",
    alphabeticLower: "$AlphabeticLower$",
    alphabeticUpper: "$AlphabeticUpper$",
    shape: "$Shape$",
    hiragana: "$Hiragana$",
    katakana: "$Katakana$",
    gender: "$Gender$",
    trafficLight: "$TrafficLight$",
    triage: "$Triage$",
    onlyCheck: "$OnlyCheck$",
} as const;
export type validateType = (typeof validateType)[keyof typeof validateType];

/**
 * フォームに応じて正規表現を返す
 * @param type 
 * @returns 
 */
export function getValidateRegex(type: validateType | undefined): RegExp | null {
    if (!type) {
        return null;
    }
    switch (type) {
        case validateType.numerics:
            return /^[0-9]*$/;
        case validateType.realNumber:
            return /^[+-]?(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)$/;
        case validateType.alphabetic:
            return /^[a-zA-Z]*$/;
        case validateType.alphabeticLower:
            return /^[a-z]*$/;
        case validateType.alphabeticUpper:
            return /^[A-Z]*$/;
        case validateType.shape:
            // 本体側で+<>=*が半角に変換されるため、暫定対策としてそれらも許可する
            // マイナス記号が分かりづらいため、一通り許容する。実機上は長音符が使われている
            // -（ハイフン/U+002D）
            // ‐（ハイフンマイナス/U+2010）
            // ‑（非区切りハイフン/U+2011）
            // –（ENヌダッシュ/U+2013）
            // —（EMダッシュ/U+2014）
            // ―（全角ダッシュ/U+2015）
            // −（全角マイナス/U+2212）
            // ー（全角長音符/U+30FC）
            // ｰ（半角長音符/U+FF70）
            // －（全角ハイフンマイナス/U+FF0D）
            return /^[＋ー±×÷＝＜＞＊☆○△+<>=*\-‐‑–—―−ｰ－]*$/;
        case validateType.hiragana:
            return /^[\u3040-\u309F]*$/;
        case validateType.katakana:
            return /^[\u30A0-\u30FF]*$/;
        default:
            return null;
    }
}

/**
 * トリアージなど、強制変換の選択肢が少ない場合にプルダウンで表示するための選択肢
 * @param type 
 * @returns 選択肢の文字列配列を返す。空文字がないとデフォルトで一つ目が表示されるので注意。
 */
export function getValidateSelection(type: validateType | undefined): string[] | null {
    if (!type) {
        return null;
    }
    switch (type) {
        case validateType.gender:
            return ["", "男", "女"];
        case validateType.trafficLight:
            return ["", "赤", "黄", "緑"];
        case validateType.triage:
            return ["", "赤", "黄", "緑", "黒"];
        default:
            return null;
    }
}

/**
 * フィールド名に含まれる強制変換のタイプを返します。
 * - フィールド名に複数のタイプが指定されていた場合、先に指定されていたタイプが優先されます。
 * @param str フィールド名
 * @returns {validateType} フィールド名に含まれる強制変換のタイプを返す
 * 例: "age$Numerics$" -> validateType.numerics
 */
export function detectValidateType(str: string): validateType {
    const attrs = Array.from(str.matchAll(/\$(.+?)\$/g), m => m[0]);
    return attrs.find(attr => Object.values(validateType).includes(attr as validateType)) as validateType;
}

export function getValidateErrorMessage(type: validateType): string {
    switch (type) {
        case validateType.numerics:
            return "数字のみ入力してください。";
        case validateType.realNumber:
            return "実数を入力してください。";
        case validateType.alphabetic:
            return "英字のみ入力してください。";
        case validateType.alphabeticLower:
            return "英小文字のみ入力してください。";
        case validateType.alphabeticUpper:
            return "英大文字のみ入力してください。";
        case validateType.shape:
            return "指定された記号（＋ー±×÷＝＜＞＊☆○△）のみ入力してください。";
        case validateType.hiragana:
            return "ひらがなのみ入力してください。";
        case validateType.katakana:
            return "カタカナのみ入力してください。";
        default:
            return "入力値が不正です。";
    }
}